import styled, { css } from 'styled-components';
import { InputSearch } from 'ui/inputs';
import { IndexTabTrigger } from 'ui/tabsGroup';
import {
  Body1,
  body2Medium,
  caption,
  colors,
  duration,
  easing,
  fontSizes,
  heading4,
  spacing,
} from 'ui/theme';
import { pxToRem } from 'utils/convert';

const singleLineOverflowEllipsis = css`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const Placements = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.quarter};
`;

export const MediaLayoutIconWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  flex-shrink: 0;
  width: ${spacing.double};
  height: ${spacing.plusHalf};
`;

export const Name = styled.div`
  ${body2Medium}
  ${singleLineOverflowEllipsis}
`;

export const Description = styled.div`
  ${caption}
  ${singleLineOverflowEllipsis}
`;

export const PlacementsListEntryContainer = styled.div<{ isSelected: boolean }>`
  display: flex;
  align-items: center;
  padding: ${spacing.base};
  gap: ${spacing.base};
  flex: 1 0 0;
  border-radius: ${spacing.half};
  transition: all ${duration.short} ${easing.entrance};
  min-height: ${pxToRem(
    70,
  )}; /** Set to 70px so with the border they will add up to 72px in height */
  border: solid ${spacing.single} transparent;

  ${MediaLayoutIconWrapper} {
    color: ${colors.iconSubtle};
    transition: all ${duration.short} ${easing.entrance};
  }

  ${Description} {
    color: ${colors.textSecondary};
  }

  ${({ isSelected }) =>
    isSelected
      ? css`
          background: ${colors.backgroundInteractive};
          border: solid ${spacing.single} ${colors.borderInteractiveSubtle};

          ${MediaLayoutIconWrapper} {
            color: ${colors.iconInteractiveSubtle};
          }

          ${Name} {
            color: ${colors.textLink};
          }

          ${Description} {
            color: ${colors.textInteractive};
          }
        `
      : css`
          &:hover {
            background: ${colors.backgroundPrimaryHover};
          }
        `}

  &:hover {
    cursor: pointer;
  }

  &:focus-visible {
    border-color: ${colors.borderInteractive};
    box-shadow: 0 0 0 ${spacing.single} ${colors.borderInteractive};
  }
`;

export const PlacementDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.quarter};
  flex: 1 0 0;
  ${singleLineOverflowEllipsis}
`;

export const PlacementDetailsContainer = styled.div`
  height: max-content;
  flex-grow: 1;
  flex-shrink: 0;
  flex-basis: 55%;
  border: solid ${spacing.single} ${colors.borderPrimary};
  border-radius: ${spacing.half};
  padding: ${spacing.plusHalf} ${spacing.plusHalf} 0 ${spacing.plusHalf};
`;

export const HeaderContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.half};
  margin-bottom: ${spacing.plusHalf};
`;

export const Header = styled.div`
  ${heading4}
  align-items: center;
`;

export const Subheader = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.quarter};
  color: ${colors.textSecondary};
`;

export const SubheaderField = styled.div`
  ${caption}
  color: ${colors.textSecondary};
  overflow-wrap: anywhere;
`;

export const DetailRow = styled.div`
  display: grid;
  grid-template-columns: 30% 1fr;
  margin: ${spacing.plusHalf} 0;
`;

export const FieldList = styled.div``;

export const AppList = styled.div`
  display: flex;
  flex-wrap: wrap;
`;

export const AppDestinationContainer = styled.div`
  display: flex;
  align-items: center;

  & > svg {
    margin: 0 ${spacing.half};
  }

  &:not(:only-of-type):not(:last-child):after {
    font-size: ${fontSizes.s16};
    content: ',';
  }
`;

export const JsonViewWrapper = styled.div`
  padding: ${spacing.base};
  border-radius: 4px;
  overflow: hidden;
  background-color: ${colors.grey1};
  border: 1px solid ${colors.grey15};
`;

export const EmptyStateContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  padding: ${spacing.x6};
  text-align: center;
`;

export const EmptyStateSecondaryText = styled(Body1)`
  color: ${colors.textSecondary};
  max-width: ${spacing.x32};
`;

export const PlacementsSubHeader = styled.div<{ isEmptyState: boolean }>`
  display: ${({ isEmptyState }) => (isEmptyState ? 'none' : 'flex')};
  justify-content: space-between;
  align-items: center;
`;

export const PlacementStatusTab = styled(IndexTabTrigger)`
  cursor: pointer;
`;

export const StyledSearch = styled(InputSearch)`
  width: ${spacing.x24};
`;

export const PlacementCreator = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.half};
  color: ${colors.textSecondary};

  & > span:first-child {
    display: none;
  }
`;
